import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { Users, Plus, Copy, Mail, RefreshCw, ShieldCheck, Landmark, WalletCards, Coins } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const statusLabel = (status) => ({
  Pending: 'Pendente',
  Active: 'Ativo',
  Rejected: 'Rejeitado',
  Blocked: 'Bloqueado',
  Closed: 'Encerrado',
  Consumed: 'Utilizado',
  Revoked: 'Revogado',
  Expired: 'Expirado',
  UnderReview: 'Em análise',
  Approved: 'Aprovado',
}[status] || status || 'Não iniciado');

const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value || 0));

const Customers = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [customers, setCustomers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [formData, setFormData] = useState({ email: '', document: '', expiresInDays: 7 });
  const [reviewCustomer, setReviewCustomer] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [riskLevel, setRiskLevel] = useState('Low');
  const [reviewNotes, setReviewNotes] = useState('');
  const [accountLabel, setAccountLabel] = useState('Conta principal');
  const [financialCustomer, setFinancialCustomer] = useState(null);
  const [financialOpen, setFinancialOpen] = useState(false);
  const [financialLoading, setFinancialLoading] = useState(false);
  const [crediting, setCrediting] = useState(false);
  const [creditAmount, setCreditAmount] = useState('100.00');
  const [balance, setBalance] = useState(null);
  const [statement, setStatement] = useState([]);

  const tenantId = user?.tenantId;

  const fetchData = async () => {
    if (!tenantId) {
      setLoading(false);
      toast({ variant: 'destructive', title: 'Sessão sem tenant', description: 'Faça login novamente para continuar.' });
      return;
    }

    setLoading(true);
    try {
      const [customersResponse, invitationsResponse] = await Promise.all([
        api.get(`/tenants/${tenantId}/customers`),
        api.get(`/tenants/${tenantId}/invitations`),
      ]);
      setCustomers(customersResponse.data || []);
      setInvitations(invitationsResponse.data || []);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Erro', description: error.response?.data?.detail || 'Não foi possível carregar clientes e convites.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const handleInvite = async (event) => {
    event.preventDefault();
    if (!tenantId || !user?.userId) {
      toast({ variant: 'destructive', title: 'Sessão inválida', description: 'Faça login novamente para gerar convites.' });
      return;
    }

    setCreating(true);
    setGeneratedLink('');
    try {
      const expiresAt = new Date(Date.now() + Number(formData.expiresInDays) * 86400000).toISOString();
      const response = await api.post(`/tenants/${tenantId}/invitations`, {
        createdByUserId: user.userId,
        expiresAt,
        maxUses: 1,
        intendedEmail: formData.email.trim() || null,
        intendedDocument: formData.document.replace(/\D/g, '') || null,
      });
      const link = `${window.location.origin}/onboarding/${response.data.token}`;
      setGeneratedLink(link);
      toast({ title: 'Convite criado', description: 'O link de onboarding foi gerado com sucesso.' });
      await fetchData();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Erro ao criar convite', description: error.response?.data?.detail || 'Não foi possível gerar o convite.' });
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async () => {
    if (!generatedLink) return;
    await navigator.clipboard.writeText(generatedLink);
    toast({ title: 'Link copiado', description: 'Envie este link ao cliente.' });
  };

  const openReview = (customer) => {
    setReviewCustomer(customer);
    setRiskLevel('Low');
    setReviewNotes('');
    setAccountLabel('Conta principal');
    setReviewOpen(true);
  };

  const reviewKyc = async (decision) => {
    if (!reviewCustomer || !tenantId || !user?.userId) return;
    setReviewing(true);
    try {
      await api.post(`/tenants/${tenantId}/customers/${reviewCustomer.customer_id}/kyc/${decision}`, {
        reviewedByUserId: user.userId,
        riskLevel,
        notes: reviewNotes || null,
      });
      toast({
        title: decision === 'approve' ? 'KYC aprovado' : 'KYC rejeitado',
        description: decision === 'approve' ? 'O cliente já pode ter o relacionamento aprovado e a conta criada.' : 'O KYC foi rejeitado e a conta permanece bloqueada.',
      });
      await fetchData();
      if (decision === 'reject') setReviewOpen(false);
      else setReviewCustomer((current) => current ? { ...current, kyc_status: 'Approved' } : current);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Erro na análise KYC', description: error.response?.data?.detail || 'Não foi possível concluir a análise.' });
    } finally {
      setReviewing(false);
    }
  };

  const createAccount = async () => {
    if (!reviewCustomer || !tenantId) return;
    setReviewing(true);
    try {
      await api.post(`/tenants/${tenantId}/relationships/${reviewCustomer.relationship_id}/approve`, {
        label: accountLabel || null,
      });
      toast({ title: 'Conta criada', description: 'Relacionamento ativado e conta virtual criada com sucesso.' });
      setReviewOpen(false);
      await fetchData();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Conta não criada', description: error.response?.data?.detail || 'Não foi possível criar a conta.' });
    } finally {
      setReviewing(false);
    }
  };

  const loadFinancial = async (customer) => {
    if (!tenantId || !customer?.account_id) return;
    setFinancialLoading(true);
    try {
      const [balanceResponse, statementResponse] = await Promise.all([
        api.get(`/tenants/${tenantId}/accounts/${customer.account_id}/balance`),
        api.get(`/tenants/${tenantId}/accounts/${customer.account_id}/statement`),
      ]);
      setBalance(balanceResponse.data);
      setStatement(statementResponse.data || []);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Erro financeiro', description: error.response?.data?.detail || 'Não foi possível consultar saldo e extrato.' });
    } finally {
      setFinancialLoading(false);
    }
  };

  const openFinancial = async (customer) => {
    setFinancialCustomer(customer);
    setBalance(null);
    setStatement([]);
    setCreditAmount('100.00');
    setFinancialOpen(true);
    await loadFinancial(customer);
  };

  const creditSandbox = async () => {
    if (!tenantId || !financialCustomer?.account_id) return;
    const amount = Number(String(creditAmount).replace(',', '.'));
    if (!Number.isFinite(amount) || amount <= 0) {
      toast({ variant: 'destructive', title: 'Valor inválido', description: 'Informe um valor maior que zero.' });
      return;
    }

    setCrediting(true);
    try {
      const reference = `SANDBOX-CREDIT-${Date.now()}`;
      await api.post(`/tenants/${tenantId}/accounts/${financialCustomer.account_id}/credits`, {
        amount,
        reference,
        description: 'Crédito de teste do Sandbox',
      });
      toast({ title: 'Crédito lançado', description: `${formatCurrency(amount)} registrado no ledger.` });
      await loadFinancial(financialCustomer);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Crédito não realizado', description: error.response?.data?.detail || 'Não foi possível registrar o crédito.' });
    } finally {
      setCrediting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="customers-page">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Clientes</h1>
          <p className="mt-2 text-muted-foreground">Convide clientes, analise KYC e acompanhe o vínculo com este tenant.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={fetchData} disabled={loading}>
            <RefreshCw className="mr-2 h-4 w-4" /> Atualizar
          </Button>
          <Dialog open={open} onOpenChange={(value) => { setOpen(value); if (!value) setGeneratedLink(''); }}>
            <DialogTrigger asChild>
              <Button className="rounded-full"><Plus className="mr-2 h-4 w-4" /> Convidar cliente</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Convidar cliente</DialogTitle>
                <DialogDescription>Gere um link individual de onboarding vinculado ao seu tenant.</DialogDescription>
              </DialogHeader>
              {!generatedLink ? (
                <form onSubmit={handleInvite} className="space-y-4">
                  <div className="space-y-2"><Label htmlFor="invite-email">E-mail</Label><Input id="invite-email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required /></div>
                  <div className="space-y-2"><Label htmlFor="invite-document">CPF/CNPJ</Label><Input id="invite-document" value={formData.document} onChange={(e) => setFormData({ ...formData, document: e.target.value })} /></div>
                  <div className="space-y-2"><Label htmlFor="invite-days">Validade em dias</Label><Input id="invite-days" type="number" min="1" max="30" value={formData.expiresInDays} onChange={(e) => setFormData({ ...formData, expiresInDays: e.target.value })} required /></div>
                  <Button type="submit" className="w-full" disabled={creating}>{creating ? 'Gerando...' : 'Gerar convite'}</Button>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-lg border bg-muted/40 p-4"><p className="mb-2 text-sm font-medium">Link de onboarding</p><p className="break-all text-sm text-muted-foreground">{generatedLink}</p></div>
                  <Button type="button" className="w-full" onClick={copyLink}><Copy className="mr-2 h-4 w-4" /> Copiar link</Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" /> Clientes vinculados</CardTitle><CardDescription>{customers.length} cliente(s) neste tenant</CardDescription></CardHeader>
          <CardContent>
            {loading ? <p className="py-8 text-center text-muted-foreground">Carregando...</p> : customers.length === 0 ? <p className="py-10 text-center text-muted-foreground">Nenhum cliente ainda.</p> : (
              <div className="space-y-3">
                {customers.map((customer) => (
                  <div key={customer.relationship_id} className="rounded-lg border p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium">{customer.name}</p>
                        <p className="text-sm text-muted-foreground">{customer.email || 'Sem e-mail'}</p>
                        <p className="mt-1 text-xs text-muted-foreground">Documento: {customer.document}</p>
                      </div>
                      <Badge variant={customer.status === 'Active' ? 'default' : 'secondary'}>{statusLabel(customer.status)}</Badge>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <Badge variant={customer.kyc_status === 'Approved' ? 'default' : 'outline'}>KYC: {statusLabel(customer.kyc_status)}</Badge>
                      <span className="text-xs text-muted-foreground">Relacionamento #{customer.relationship_number} · Contas: {customer.account_count}</span>
                    </div>
                    {customer.account_id && (
                      <p className="mt-2 text-xs text-muted-foreground">Conta {String(customer.account_number || '').padStart(8, '0')}-{customer.account_check_digit}</p>
                    )}
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => openReview(customer)}><ShieldCheck className="mr-2 h-4 w-4" /> {customer.kyc_status === 'Approved' ? 'Ver KYC' : 'Analisar KYC'}</Button>
                      {customer.account_id && (
                        <Button size="sm" onClick={() => openFinancial(customer)}><WalletCards className="mr-2 h-4 w-4" /> Saldo e extrato</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" /> Convites</CardTitle><CardDescription>Histórico recente de convites do tenant</CardDescription></CardHeader>
          <CardContent>
            {loading ? <p className="py-8 text-center text-muted-foreground">Carregando...</p> : invitations.length === 0 ? <p className="py-10 text-center text-muted-foreground">Nenhum convite criado.</p> : (
              <div className="space-y-3">{invitations.map((invitation) => (
                <div key={invitation.id} className="flex items-center justify-between gap-4 rounded-lg border p-4">
                  <div><p className="font-medium">{invitation.intended_email || 'Convite sem e-mail definido'}</p><p className="text-xs text-muted-foreground">Expira em {new Date(invitation.expires_at).toLocaleString('pt-BR')}</p></div>
                  <Badge variant={invitation.status === 'Active' ? 'default' : 'secondary'}>{statusLabel(invitation.status)}</Badge>
                </div>
              ))}</div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader><DialogTitle>Análise KYC</DialogTitle><DialogDescription>{reviewCustomer ? `${reviewCustomer.name} · ${reviewCustomer.document}` : ''}</DialogDescription></DialogHeader>
          {reviewCustomer && (
            <div className="space-y-5">
              <div className="rounded-lg border p-4"><p className="text-sm font-medium">Situação atual</p><div className="mt-2 flex gap-2"><Badge>KYC: {statusLabel(reviewCustomer.kyc_status)}</Badge><Badge variant="outline">Relacionamento: {statusLabel(reviewCustomer.status)}</Badge></div></div>
              {reviewCustomer.kyc_status !== 'Approved' && reviewCustomer.kyc_status !== 'Rejected' && (
                <>
                  <div className="space-y-2"><Label>Nível de risco</Label><Select value={riskLevel} onValueChange={setRiskLevel}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Low">Baixo</SelectItem><SelectItem value="Medium">Médio</SelectItem><SelectItem value="High">Alto</SelectItem></SelectContent></Select></div>
                  <div className="space-y-2"><Label>Observações da análise</Label><Textarea value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} placeholder="Registre os elementos considerados na análise." /></div>
                  <div className="grid grid-cols-2 gap-2"><Button variant="destructive" disabled={reviewing} onClick={() => reviewKyc('reject')}>Rejeitar KYC</Button><Button disabled={reviewing} onClick={() => reviewKyc('approve')}>Aprovar KYC</Button></div>
                </>
              )}
              {reviewCustomer.kyc_status === 'Approved' && reviewCustomer.status !== 'Active' && reviewCustomer.account_count === 0 && (
                <div className="space-y-3 rounded-lg border p-4">
                  <div><p className="font-medium">KYC aprovado</p><p className="text-sm text-muted-foreground">Agora é possível ativar o relacionamento e criar a conta virtual.</p></div>
                  <div className="space-y-2"><Label>Identificação da conta</Label><Input value={accountLabel} onChange={(e) => setAccountLabel(e.target.value)} /></div>
                  <Button className="w-full" disabled={reviewing} onClick={createAccount}><Landmark className="mr-2 h-4 w-4" /> Aprovar relacionamento e criar conta</Button>
                </div>
              )}
              {reviewCustomer.kyc_status === 'Approved' && reviewCustomer.status === 'Active' && <p className="text-sm text-muted-foreground">KYC aprovado. O relacionamento já está ativo.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={financialOpen} onOpenChange={setFinancialOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader><DialogTitle>Validação do ledger</DialogTitle><DialogDescription>{financialCustomer ? `${financialCustomer.name} · conta ${String(financialCustomer.account_number || '').padStart(8, '0')}-${financialCustomer.account_check_digit}` : ''}</DialogDescription></DialogHeader>
          {financialCustomer && (
            <div className="space-y-5">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border p-4"><p className="text-sm text-muted-foreground">Saldo calculado pelo ledger</p><p className="mt-1 text-2xl font-bold">{financialLoading ? '...' : formatCurrency(balance?.balance)}</p></div>
                <div className="rounded-lg border p-4"><p className="text-sm text-muted-foreground">Moeda</p><p className="mt-1 text-2xl font-bold">{balance?.currency || 'BRL'}</p></div>
              </div>

              <div className="rounded-lg border p-4">
                <div className="mb-3 flex items-center gap-2"><Coins className="h-5 w-5" /><p className="font-medium">Crédito de teste do Sandbox</p></div>
                <p className="mb-3 text-sm text-muted-foreground">Este crédito gera uma partida dupla no ledger. Não altera um campo de saldo diretamente.</p>
                <div className="flex gap-2"><Input type="number" min="0.01" step="0.01" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} /><Button disabled={crediting} onClick={creditSandbox}>{crediting ? 'Lançando...' : 'Creditar'}</Button></div>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between"><p className="font-medium">Extrato</p><Button size="sm" variant="outline" onClick={() => loadFinancial(financialCustomer)} disabled={financialLoading}><RefreshCw className="mr-2 h-4 w-4" /> Atualizar</Button></div>
                {financialLoading ? <p className="py-6 text-center text-muted-foreground">Consultando ledger...</p> : statement.length === 0 ? <p className="rounded-lg border py-6 text-center text-muted-foreground">Nenhum lançamento.</p> : (
                  <div className="space-y-2">{statement.map((item) => (
                    <div key={item.journalEntryId} className="flex items-center justify-between rounded-lg border p-3">
                      <div><p className="font-medium">{item.description}</p><p className="text-xs text-muted-foreground">{new Date(item.occurredAt).toLocaleString('pt-BR')} · {item.direction}</p></div>
                      <p className={`font-mono font-semibold ${Number(item.amount) >= 0 ? '' : 'text-destructive'}`}>{Number(item.amount) >= 0 ? '+' : ''}{formatCurrency(item.amount)}</p>
                    </div>
                  ))}</div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
